import ApplicationController from './application_controller'
import { Turbo } from '@hotwired/turbo-rails'
import { confirm } from '../utils/turbo_confirm'
import Logger from '../lib/logger'

export default class extends ApplicationController {
  static targets = ['template', 'container', 'addButton']
  static values = { 
    association: String,
    partial: String,
    wrapperClass: { type: String, default: 'nested-fields' }
  }

  connect() {
    super.connect()
    
    // Prevent multiple connections
    if (this._connected) {
      Logger.debug('Controller already connected, skipping...', { controller: this.constructor.name })
      return
    }
    
    this._connected = true
  }

  disconnect() {
    this._connected = false
    super.disconnect?.()
  }

  /**
   * Action to remove a nested field.
   * Can be called via Stimulus action (data-action="click->nested-form#remove")
   * or directly with a wrapper element.
   */
  async remove(eventOrWrapper) {
    let wrapper
    let removeButton

    if (eventOrWrapper instanceof Event) {
      eventOrWrapper.preventDefault()
      // currentTarget is the element with the data-action
      removeButton = eventOrWrapper.currentTarget
      // Find the closest wrapper relative to the button
      wrapper = removeButton.closest(`.${this.wrapperClassValue}`)
    } else if (eventOrWrapper instanceof Element) {
      wrapper = eventOrWrapper
      removeButton = wrapper.querySelector('.remove-nested-fields')
    }
    
    if (!wrapper) {
      Logger.error('Could not find nested-fields wrapper', { controller: this.constructor.name })
      return
    }

    // Check for confirmation if specified
    if (removeButton) {
      const confirmationMessage = removeButton.dataset.turboConfirm || removeButton.dataset.confirm
      if (confirmationMessage) {
        try {
          // Use the exported confirm helper instead of Turbo.confirm
          const confirmed = await confirm(confirmationMessage, removeButton)
          if (!confirmed) return
        } catch (error) {
          Logger.error('Confirmation failed', error, { controller: this.constructor.name })
          return
        }
      }
    }

    this.performRemoval(wrapper)
  }

  /**
   * Performs the actual removal or marking for destruction
   */
  performRemoval(wrapper) {
    const isNewRecord = wrapper.dataset.newRecord === 'true'
    
    if (isNewRecord) {
      // For new records, we can safely remove the DOM element directly
      if (wrapper.id) {
        const turboStream = `<turbo-stream action="remove" target="${wrapper.id}"></turbo-stream>`
        Turbo.renderStreamMessage(turboStream)
      } else {
        wrapper.remove()
      }
    } else {
      // Mark for destruction (hide and add _destroy field)
      this.markForDestruction(wrapper)
    }
    
    // Trigger custom event for other controllers
    this.element.dispatchEvent(new CustomEvent('nested-form:removed', {
      detail: { 
        association: this.associationValue,
        wrapper: wrapper,
        isNewRecord: isNewRecord
      }
    }))
  }

  markForDestruction(wrapper) {
    // Hide the wrapper
    wrapper.style.display = 'none'
    
    // Add or update _destroy field
    let destroyField = wrapper.querySelector('input[name*="_destroy"]')
    
    if (!destroyField) {
      destroyField = document.createElement('input')
      destroyField.type = 'hidden'
      destroyField.name = this.getDestroyFieldName(wrapper)
      wrapper.appendChild(destroyField)
    }
    
    destroyField.value = '1'
  }

  getDestroyFieldName(wrapper) {
    // Extract the association name from existing field names
    const existingField = wrapper.querySelector('input, select, textarea')
    if (existingField && existingField.name) {
      // Matches pattern like model[association_attributes][0][field]
      const match = existingField.name.match(/\[([^\]]+)\]\[([^\]]+)\]/)
      if (match) {
        return `${match[1]}[${match[2]}][_destroy]`
      }
    }
    
    // Fallback
    return `${this.associationValue}[_destroy]`
  }

  // Handle Turbo Stream add (this will be called by the server response)
  addViaTurbo(event) {
    // The server will handle adding the form via Turbo Stream
    // This method is just a placeholder for the data-action
  }
}
